import React from 'react';
import type {
  CreateTicketOutput,
  RouteToSupportOutput,
} from '../../types/conciergeToolTypes';
import { SupportTicketCardChrome } from './SupportTicketCardChrome';
import type { SupportTicketCardStyles } from './supportTicketCardConfig';

/**
 * Renders the result of a successful ticket creation. Driven by either:
 *  - a `tool-create_ticket` part (direct creation)
 *  - a `tool-route_to_support` part whose dispatch landed on the live_support
 *    branch and produced a ticket
 *
 * Visually consistent: small neutral card with status pill, title, and a
 * "View" affordance when a ticketUrl is provided.
 */

export interface SupportTicketCardProps {
  status: 'success' | 'unavailable' | 'cannot_submit';
  message: string;
  ticketId?: string;
  displayId?: string;
  ticketUrl?: string;
  ticketState?: string;
  ticketTypeName?: string;
  category?: string;
  nextSteps?: string;
}

export function SupportTicketCard(props: SupportTicketCardProps) {
  const { status, ticketId } = props;
  if (status !== 'success' || !ticketId) return null;

  return (
    <SupportTicketCardChrome>
      {(styles) => <SupportTicketCardView {...props} styles={styles} />}
    </SupportTicketCardChrome>
  );
}

function SupportTicketCardView({
  ticketId,
  displayId,
  ticketUrl,
  ticketState,
  ticketTypeName,
  category,
  nextSteps,
  message,
  styles,
}: SupportTicketCardProps & { styles: SupportTicketCardStyles }) {
  const statePill = ticketState ? humanizeState(ticketState) : 'Submitted';
  const ref = displayId ?? ticketId;
  const body = nextSteps || message;

  return (
    <div
      className="tw:my-2 tw:border tw:shadow-sm"
      style={{
        borderRadius: styles.cardBorderRadius,
        borderColor: styles.cardBorderColor,
        backgroundColor: styles.cardBgColor,
        padding: styles.cardPadding,
      }}
      role="group"
      aria-label="Support ticket card"
    >
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <div className="tw:flex tw:items-center tw:gap-2">
            <span
              className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide"
              style={{ color: styles.labelColor }}
            >
              Ticket
            </span>
            <span
              className="tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium"
              style={{
                backgroundColor: 'var(--bc-support-bg)',
                color: 'var(--bc-support-text)',
              }}
            >
              {statePill}
            </span>
          </div>
          <div
            className="tw:font-semibold"
            style={{ fontSize: styles.refFontSize, color: styles.refColor }}
          >
            #{ref}
          </div>
          {(ticketTypeName || category) && (
            <div style={{ fontSize: styles.metaFontSize, color: styles.metaColor }}>
              {[ticketTypeName, category].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        {ticketUrl && (
          <a
            href={ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:border tw:px-3 tw:py-1.5 tw:font-semibold tw:no-underline"
            style={{
              borderRadius: styles.viewBorderRadius,
              borderColor: styles.viewBorderColor,
              backgroundColor: styles.viewBgColor,
              color: styles.viewTextColor,
              fontSize: styles.viewFontSize,
            }}
          >
            View
          </a>
        )}
      </div>
      {body && (
        <p
          className="tw:mt-3 tw:leading-relaxed"
          style={{ fontSize: styles.bodyFontSize, color: styles.bodyColor }}
        >
          {body}
        </p>
      )}
    </div>
  );
}

/**
 * Adapter for the legacy `tool-route_to_support` part shape — when the LLM
 * called `route_to_support` and the dispatch produced a live ticket, the
 * tool's text output contains a SUCCESS line plus the ticket id. We render
 * the same card with a best-effort parse.
 */
export function SupportTicketCardFromRouteToSupport({
  output,
}: {
  output: RouteToSupportOutput | string | undefined;
}) {
  if (!output) return null;
  const text = typeof output === 'string' ? output : (output as any).reason ?? '';
  if (!text) return null;
  // Heuristic: if the dispatch returned a SUCCESS string with "Ticket {id}",
  // surface it as a card; otherwise leave to TextPart.
  const match = /Ticket\s+#?([A-Za-z0-9_-]+)/.exec(text);
  if (!match) return null;
  return (
    <SupportTicketCard
      status="success"
      message={text}
      ticketId={match[1]}
      displayId={match[1]}
      ticketState="submitted"
      nextSteps={text.replace(/^SUCCESS:\s*/, '')}
    />
  );
}

export function SupportTicketCardFromCreateTicket({
  output,
}: {
  output: CreateTicketOutput | undefined;
}) {
  if (!output) return null;
  return (
    <SupportTicketCard
      status={output.status}
      message={output.message}
      ticketId={output.ticketId}
      displayId={output.displayId}
      ticketUrl={output.ticketUrl}
      ticketState={output.ticketState}
      ticketTypeName={output.ticketTypeName}
      category={output.category}
      nextSteps={output.nextSteps}
    />
  );
}

function humanizeState(state: string): string {
  switch (state) {
    case 'submitted':
      return 'Submitted';
    case 'in_progress':
      return 'In progress';
    case 'waiting_on_customer':
      return 'Waiting on you';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
    default:
      return state.replace(/_/g, ' ');
  }
}
